import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { Phone } from "lucide-react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { ContactEditModal } from "@/components/portal/ContactEditModal"
import { HeroLinkModal } from "@/components/portal/HeroLinkModal"
import { BookingModal } from "@/components/portal/BookingModal"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { HeroInfoPopover } from "@/components/portal/HeroInfoPopover"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { cn } from "@/lib/utils"

type MeitiItem = NonNullable<Awaited<ReturnType<typeof fetchMeiti>>>["items"][number] & {
  /** HERO-Bezeichnung des Vorgangs aus dem lokalen Projekt-Cache — von Hand
   *  ergaenzt, weil `api-types.ts` generiert ist. */
  hero_project_nr?: string | null
  hero_project_title?: string | null
}

async function fetchMeiti(tenant: string) {
  const { data, error } = await api.GET("/api/v1/calls", {
    params: { query: { tenant: tenant || undefined, limit: 200 } },
  })
  if (error) throw new Error(String(error))
  return data
}

type MeitiFilter = null | "incoming" | "open" | "booked" | "done"

/** KI-Telefon-Liste — Card-Layout 1:1 wie admin/calls.py. */
export default function MeitiCallsList() {
  const [tenant, setTenant] = useTenantFilter()
  const [filter, setFilter] = useState<MeitiFilter>(null)
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["meiti-calls", tenant],
    queryFn: () => fetchMeiti(tenant),
  })

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })

  const all = data?.items || []

  const stats = useMemo(() => ({
    incoming: all.length,
    open: all.filter((c) => c.status === "open").length,
    booked: all.filter((c) => c.status === "booked").length,
    done: all.filter((c) => c.status === "done").length,
  }), [all])

  const filtered = useMemo(() => {
    if (!filter) return all
    if (filter === "incoming") return all
    return all.filter((c) => c.status === filter)
  }, [all, filter])

  const groups = useMemo(() => {
    const m = new Map<string, MeitiItem[]>()
    for (const c of filtered) {
      const key = c.tenant_slug || "(ohne Tenant)"
      const arr = m.get(key) ?? []; arr.push(c); m.set(key, arr)
    }
    return Array.from(m.entries())
  }, [filtered])

  const markDone = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await api.PATCH("/api/v1/calls/{call_id}", {
        params: { path: { call_id: id } },
        body: { status: "done" },
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meiti-calls"] }),
  })

  const syncAll = useMutation({
    mutationFn: async () => {
      const r = await fetch("/admin/sync/meiti-now", { method: "POST", credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meiti-calls"] })
      toast("✓ Meiti-Sync läuft")
    },
    onError: (e) => toast("Sync fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  if (isLoading) return <Loading label="Lädt KI-Telefon-Anrufe..." />
  if (error) return <div className="pp-main"><ErrorBox error={error} /></div>

  return (
    <>
      <PageHeader title="Meiti Anrufe" count={all.length} onRefresh={() => refetch()} refreshing={isFetching}>
        <button
          type="button"
          onClick={() => syncAll.mutate()}
          disabled={syncAll.isPending}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          title="iCal-Feed neu abrufen"
        >
          {syncAll.isPending ? "Sync..." : "Sync jetzt"}
        </button>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success">
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} /> live
        </span>
      </PageHeader>

      <div className="pp-filter-bar">
        <select
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="">Alle Tenants</option>
          {tenantsDd.data?.items?.map((t) => (
            <option key={t.slug} value={t.slug}>{t.name}</option>
          ))}
        </select>
        <div className="sg-stats">
          <button className={cn("sg-stat sg-stat-in", filter === "incoming" && "active")} onClick={() => setFilter(filter === "incoming" ? null : "incoming")}>{stats.incoming} Eingehend</button>
          <button className={cn("sg-stat", filter === "open" && "active")} style={{ background: "rgba(245,158,11,0.12)", color: "#92400e" }} onClick={() => setFilter(filter === "open" ? null : "open")}>{stats.open} Offen</button>
          <button className={cn("sg-stat sg-stat-out", filter === "booked" && "active")} onClick={() => setFilter(filter === "booked" ? null : "booked")}>{stats.booked} Gebucht</button>
          <button className={cn("sg-stat sg-stat-done", filter === "done" && "active")} onClick={() => setFilter(filter === "done" ? null : "done")}>{stats.done} Erledigt</button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="pp-card" style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
          Keine Anrufe gefunden.
        </div>
      ) : (
        groups.map(([slug, items]) => {
          const offen = items.filter((c) => c.status !== "done").length
          return (
            <div key={slug} className="sg-tenant-group">
              <div className="sg-tenant-header">
                <span>{slug.toUpperCase()}</span>
                {offen > 0 && <span className="sg-count-badge">{offen} offen</span>}
              </div>
              {items.map((c) => (
                <MeitiCard key={c.id} call={c} onDone={() => markDone.mutate(c.id)} pending={markDone.isPending} />
              ))}
            </div>
          )
        })
      )}
    </>
  )
}

function MeitiCard({ call: c, onDone, pending }: { call: MeitiItem; onDone: () => void; pending: boolean }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [projectOnly, setProjectOnly] = useState(false)
  const [heroOpen, setHeroOpen] = useState(false)
  const [editContactOpen, setEditContactOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const isDone = c.status === "done"

  const unlink = async () => {
    if (!confirm("HERO-Verknüpfung trennen?")) return
    try {
      const res = await fetch(`/api/v1/calls/${c.id}/clear-hero-link`, { method: "POST", credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      qc.invalidateQueries({ queryKey: ["meiti-calls"] })
    } catch (e) {
      alert("Fehler: " + (e instanceof Error ? e.message : String(e)))
    }
  }

  const doDelete = async () => {
    if (!confirm(`Anruf von ${c.phone} wirklich löschen?`)) return
    await fetch(`/api/v1/calls/${c.id}`, { method: "DELETE", credentials: "include" })
    qc.invalidateQueries({ queryKey: ["meiti-calls"] })
  }
  const doSync = async () => {
    try {
      // sync-hero hatte kein Backend-Pendant → no-op-Refresh, ggf. spaeter neu bauen
      void c
      // await fetch(`/admin/calls/${c.id}/sync-hero`, { method: "POST", credentials: "include" })
      qc.invalidateQueries({ queryKey: ["meiti-calls"] })
    } catch (e) { alert("Sync fehlgeschlagen: " + (e instanceof Error ? e.message : String(e))) }
  }
  const transcriptName = `${c.first_name} ${c.last_name}`.trim()
  const heroName = (c.hero_contact_name || "").trim()
  const name = transcriptName || heroName || "(unbekannt)"
  const nameMismatch = !!heroName && !!transcriptName && heroName.toLowerCase() !== transcriptName.toLowerCase()
  const T = (c.hero_booking_id ? "done" : "")

  return (
    <div className={cn("sg-card", isDone && "sg-done", `status-${c.status}`)}>
      <div className="sg-top">
        <div className="sg-left">
          <div className="sg-avatar">
            <Phone className="w-[18px] h-[18px] text-[#4f46e5]" />
          </div>
          <div className="sg-info">
            <div className="sg-name-row">
              <span className="sg-name">{name}</span>
              <StatusBadgeMeiti status={c.status} />
              <DringlichkeitBadge value={c.dringlichkeit} />
              {c.call_count >= 2 && c.status !== "done" && (
                <span className="sg-badge-inline" style={{ background: "rgba(245,158,11,0.12)", color: "#92400e", border: "1px solid rgba(245,158,11,0.35)" }}>
                  {c.call_count}x angerufen
                </span>
              )}
            </div>
            <div className="sg-phone">
              <a href={`tel:${c.phone}`}>{c.phone}</a>
            </div>
            {nameMismatch && (
              <div
                style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}
                title="Der HERO-Kontakt, der mit diesem Anruf verknüpft ist, heißt anders als der Name aus dem Meiti-Transkript. Ggf. falsche Zuordnung prüfen."
              >
                ⚠ HERO-Kontakt: <strong>{heroName}</strong> (nicht {transcriptName})
              </div>
            )}
          </div>
        </div>
        <div className="sg-right">
          <div className="sg-time">{fmtRelative(c.called_at)}</div>
          <div className="sg-steps">
            <span className={cn("sg-dot", c.hero_contact_id && "done")}>K</span>
            <span className={cn("sg-dot", c.hero_project_id && "done", c.hero_contact_id && !c.hero_project_id && "next")}>P</span>
            <span className={cn("sg-dot", T === "done" && "done", c.hero_project_id && !c.hero_booking_id && "next")}>T</span>
          </div>
          {(c.hero_contact_id || c.hero_project_id) && (
            <div className="sg-hero-links">
              {c.hero_contact_id && (
                <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                  <a href={`/admin/open-hero/contact/${c.hero_contact_id}?tenant=${c.tenant_slug}`} target="_blank" onClick={(e) => e.stopPropagation()}>
                    Kontakt #{c.hero_contact_id}
                  </a>
                </HeroInfoPopover>
              )}
              {c.hero_contact_id && c.hero_project_id && " · "}
              {c.hero_project_id && (
                <HeroInfoPopover tenantSlug={c.tenant_slug || ""} contactId={c.hero_contact_id} projectId={c.hero_project_id}>
                  <a href={`/admin/open-hero/project/${c.hero_project_id}?tenant=${c.tenant_slug}`} target="_blank" onClick={(e) => e.stopPropagation()}>
                    <HeroVorgangName tenantSlug={c.tenant_slug} projectId={c.hero_project_id} nr={c.hero_project_nr} titel={c.hero_project_title} />
                  </a>
                </HeroInfoPopover>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Anfrage-Zeile */}
      {c.anfrage && (
        <div style={{ marginTop: 8, fontSize: 13, color: "var(--text)" }}>
          {c.anfrage}
        </div>
      )}
      {/* Zusammenfassung in grau-Box */}
      {c.zusammenfassung && (
        <div style={{
          marginTop: 8, padding: "10px 14px", background: "#f1f5f9",
          borderRadius: 6, fontSize: 12.5, lineHeight: 1.6, color: "var(--text)",
          whiteSpace: "pre-wrap",
        }}>
          {c.zusammenfassung}
        </div>
      )}
      {!c.zusammenfassung && !c.anfrage && (
        <div style={{
          marginTop: 8, padding: "10px 14px", background: "#fef9c3",
          border: "1px solid #fde68a", borderRadius: 6,
          fontSize: 12, color: "#92400e",
        }}>
          Keine Zusammenfassung vorhanden. Bitte in Meiti prüfen ob Chatverläufe vorhanden sind.
        </div>
      )}

      <div className="sg-actions">
        <div className="sg-actions-left">
          {c.hero_contact_id ? (
            <>
              <button type="button" className="sg-btn sg-btn-ghost" style={{ color: "var(--accent-color)" }}
                onClick={(e) => { e.stopPropagation(); setEditContactOpen(true) }}>Kontakt bearbeiten</button>
              <button type="button" className="sg-btn sg-btn-ghost"
                onClick={(e) => { e.stopPropagation(); setProjectOnly(false); setCreateOpen(true) }}>Neuer Kontakt</button>
              <button type="button" className="sg-btn sg-btn-ghost" style={{ color: "#dc2626" }}
                onClick={(e) => { e.stopPropagation(); unlink() }}>Trennen</button>
              {!c.hero_project_id ? (
                <>
                  <button type="button" className="sg-btn"
                    style={{ background: "#10b981", color: "#fff", borderColor: "#10b981" }}
                    onClick={(e) => { e.stopPropagation(); setProjectOnly(true); setCreateOpen(true) }}>Projekt anlegen</button>
                  <button type="button" className="sg-btn sg-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); setHeroOpen(true) }}>Projekt verknüpfen</button>
                </>
              ) : (
                !c.hero_booking_id && (
                  <button type="button" className="sg-btn"
                    style={{ background: "#f59e0b", color: "#fff", borderColor: "#f59e0b" }}
                    onClick={(e) => { e.stopPropagation(); setBookOpen(true) }}>Termin buchen</button>
                )
              )}
              {c.hero_booking_id && (
                <span
                  className="sg-badge-inline"
                  style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }}
                  title={`HERO-Termin ID ${c.hero_booking_id}`}
                >
                  ✓ {fmtBookingTime(c.booking_start_time, c.booking_end_time) || "Termin gebucht"}
                </span>
              )}
            </>
          ) : (
            <button
              type="button"
              className="sg-btn sg-btn-hero"
              onClick={(e) => { e.stopPropagation(); setCreateOpen(true) }}
            >Kontakt + Projekt</button>
          )}
        </div>
        <div className="sg-actions-right">
          {!isDone ? (
            <button onClick={onDone} disabled={pending} className="sg-btn sg-btn-done">Erledigt</button>
          ) : (
            <span className="sg-badge-inline sg-badge-done">✓ Erledigt</span>
          )}
          <button type="button" className="sg-btn sg-btn-ghost"
            onClick={(e) => { e.stopPropagation(); setTaskOpen(true) }}>Aufgabe</button>
          <button type="button" className="sg-btn sg-btn-ghost"
            onClick={(e) => { e.stopPropagation(); doSync() }}>Sync</button>
          <button type="button" className="sg-btn sg-btn-ghost" style={{ color: "#dc2626" }}
            onClick={(e) => { e.stopPropagation(); doDelete() }}>Löschen</button>
        </div>
      </div>
      <HeroLinkModal
        open={heroOpen}
        onClose={() => setHeroOpen(false)}
        mode="meiti"
        itemId={c.id}
        tenantSlug={c.tenant_slug}
        currentContactId={c.hero_contact_id ? Number(c.hero_contact_id) : null}
        currentContactName={name}
        currentProjectId={c.hero_project_id}
        invalidateKeys={[["meiti-calls"]]}
      />
      <ContactEditModal
        open={editContactOpen}
        onClose={() => setEditContactOpen(false)}
        contactId={c.hero_contact_id ? Number(c.hero_contact_id) : null}
        tenantSlug={c.tenant_slug || ""}
        invalidateKeys={[["meiti-calls"]]}
      />
      <BookingModal
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        emailId={c.id}
        mode="meiti"
        defaultTitle={name}
        tenantSlug={c.tenant_slug}
        heroContactId={c.hero_contact_id ? Number(c.hero_contact_id) : null}
        heroProjectId={c.hero_project_id}
        invalidateKeys={[["meiti-calls"], ["calendar"]]}
      />
      <CallTaskModal
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
        mode="meiti"
        callId={c.id}
        tenantSlug={c.tenant_slug}
        defaultTitle={`Rückruf: ${name}`}
      />
      <CreateContactModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setProjectOnly(false) }}
        mode="meiti"
        itemId={c.id}
        tenantSlug={c.tenant_slug}
        prefillPhone={c.phone || ""}
        prefillFirst={c.first_name || ""}
        prefillLast={c.last_name || ""}
        prefillDescription={c.anfrage || c.zusammenfassung || ""}
        initialWithProject={true}
        projectOnly={projectOnly}
        onSuccess={() => qc.invalidateQueries({ queryKey: ["meiti-calls"] })}
      />
    </div>
  )
}

function fmtBookingTime(start?: string | null, end?: string | null): string {
  if (!start) return ""
  try {
    const s = new Date(start)
    const datum = s.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
    const uhrS = s.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    if (!end) return `${datum} ${uhrS}`
    const e = new Date(end)
    const uhrE = e.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    return `${datum} ${uhrS}–${uhrE}`
  } catch {
    return ""
  }
}

function DringlichkeitBadge({ value }: { value: string | null | undefined }) {
  if (!value) return null
  const v = value.toLowerCase()
  const isUrgent = v.includes("hoch") || v.includes("notfall") || v.includes("kritisch")
  const color = isUrgent ? "#dc2626" : "#d97706"
  return (
    <span className="sg-badge-inline" style={{ background: `${color}1a`, color, border: `1px solid ${color}55`, textTransform: "uppercase" }}>
      {value}
    </span>
  )
}

function StatusBadgeMeiti({ status }: { status: string }) {
  const s = (status || "").toLowerCase()
  if (s === "done") return <span className="sg-badge-inline sg-badge-done">Erledigt</span>
  if (s === "booked") return <span className="sg-badge-inline sg-badge-answered">Gebucht</span>
  if (s === "no_booking") return <span className="sg-badge-inline sg-badge-missed">Keine Buchung</span>
  if (s === "open") return <span className="sg-badge-inline" style={{ background: "rgba(79,70,229,0.08)", color: "#4f46e5", border: "1px solid rgba(79,70,229,0.25)" }}>Offen</span>
  return <span className="sg-badge-inline">{status || "—"}</span>
}
